import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatGbp } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
    ExternalLink, Loader2, ShieldCheck, Sparkles, Gavel,
    PoundSterling, Activity, Info, TrendingUp,
} from "lucide-react";

function MethodologyBadge({ factual }) {
    return factual ? (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#DCFCE7] text-[#166534]"
            data-testid="methodology-factual-badge"
        >
            <ShieldCheck className="w-3 h-3" /> Disclosed
        </span>
    ) : (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#FEF3C7] text-[#92400E]"
            data-testid="methodology-ai-badge"
        >
            <Sparkles className="w-3 h-3" /> AI estimate
        </span>
    );
}

function SourceLink({ href, children, testid }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1A4331] underline underline-offset-4 decoration-[#1A4331]/30 hover:decoration-[#1A4331] hover:text-[#123022]"
            data-testid={testid}
        >
            {children} <ExternalLink className="w-3.5 h-3.5" />
        </a>
    );
}

function DisclosureSection({ d }) {
    if (!d?.matched) {
        return (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                <header className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                        <PoundSterling className="w-4 h-4 text-[#1A4331]" />
                        <h3 className="font-heading text-base font-medium text-[#1A4331]">ABPI Disclosure UK</h3>
                    </div>
                    <MethodologyBadge factual />
                </header>
                <p className="text-sm text-[#475569] leading-relaxed">
                    No disclosure record found for <strong>{d?.queried_company || "this company"}</strong>.
                    Company may be smaller than ABPI reporting threshold, trade under a different legal entity,
                    or not be an ABPI member.
                </p>
                <SourceLink href={d?.source_url} testid="duk-manual-search-link">
                    Search Disclosure UK manually
                </SourceLink>
            </div>
        );
    }

    const yearly = d.yearly_totals || [];
    const latest = yearly[yearly.length - 1];
    const chartData = yearly.map((y) => ({ year: y.year, total: Math.round(y.total_gbp) }));
    const rrt = d.rcn_relevant_totals || {};

    const buckets = [
        { key: "event_sponsorship_gbp", label: "Event sponsorship", rcnHint: true },
        { key: "donations_grants_gbp", label: "Donations & grants" },
        { key: "contracted_services_gbp", label: "Contracted services" },
        { key: "collaborative_working_gbp", label: "Collaborative working" },
        { key: "fees_gbp", label: "HCP fees" },
    ].filter((b) => (rrt[b.key] || 0) > 0);

    return (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="duk-section">
            <header className="flex items-start justify-between gap-2 mb-3">
                <div>
                    <div className="flex items-center gap-2">
                        <PoundSterling className="w-4 h-4 text-[#1A4331]" />
                        <h3 className="font-heading text-base font-medium text-[#1A4331]">ABPI Disclosure UK</h3>
                    </div>
                    <p className="text-xs text-[#8A9A86] mt-0.5">
                        Matched: <span className="font-medium text-[#1E293B]" data-testid="duk-matched-name">{d.matched_company}</span>
                    </p>
                </div>
                <MethodologyBadge factual />
            </header>

            {latest && (
                <div className="rounded-lg bg-[#F8F7F4] p-4 mb-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#8A9A86]">
                        Latest disclosed total ({latest.year})
                    </p>
                    <p className="font-heading text-3xl font-semibold text-[#1A4331] mt-1" data-testid="duk-latest-total">
                        {formatGbp(latest.total_gbp)}
                    </p>
                    <p className="text-[11px] text-[#475569] mt-1">
                        Actual transfers of value to UK healthcare organisations & professionals
                    </p>
                </div>
            )}

            {chartData.length > 1 && (
                <div className="h-40 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis dataKey="year" tick={{ fill: "#475569", fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatGbp(v)} />
                            <Tooltip
                                contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 }}
                                formatter={(v) => formatGbp(v)}
                            />
                            <Bar dataKey="total" name="Disclosed £" fill="#1A4331" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {buckets.length > 0 && (
                <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-[#8A9A86] mb-2">
                        Breakdown — most relevant to RCN products
                    </p>
                    <ul className="space-y-1.5" data-testid="duk-buckets">
                        {buckets.map((b) => (
                            <li key={b.key} className="flex items-center justify-between text-sm border-b border-[#E2E8F0]/50 pb-1.5 last:border-0">
                                <span className="text-[#1E293B]">
                                    {b.label}
                                    {b.rcnHint && (
                                        <Badge className="ml-2 bg-[#1A4331]/10 text-[#1A4331] hover:bg-[#1A4331]/10 border-0 text-[10px]">
                                            RCN event signal
                                        </Badge>
                                    )}
                                </span>
                                <span className="font-mono text-[#1A4331] font-semibold">{formatGbp(rrt[b.key])}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <Separator className="my-3" />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <SourceLink href={d.company_url} testid="duk-company-link">
                    Full profile on Disclosure UK
                </SourceLink>
                <SourceLink href={d.source_url} testid="duk-source-link">
                    All matches
                </SourceLink>
            </div>
        </div>
    );
}

function OpenPrescribingSection({ op }) {
    return (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="op-section">
            <header className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-[#1A4331]" />
                    <h3 className="font-heading text-base font-medium text-[#1A4331]">
                        NHS prescribing — OpenPrescribing
                    </h3>
                </div>
                <MethodologyBadge factual />
            </header>
            <p className="text-sm text-[#475569] leading-relaxed mb-3">{op.note}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
                {op.actions.map((a) => (
                    <SourceLink key={a.url} href={a.url} testid={`op-link-${a.label}`}>{a.label}</SourceLink>
                ))}
            </div>
        </div>
    );
}

function PmcpaSection({ p }) {
    return (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="pmcpa-section">
            <header className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Gavel className="w-4 h-4 text-[#1A4331]" />
                    <h3 className="font-heading text-base font-medium text-[#1A4331]">
                        PMCPA compliance history
                    </h3>
                </div>
                <MethodologyBadge factual />
            </header>
            <p className="text-sm text-[#475569] leading-relaxed mb-3">{p.note}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
                {p.actions.map((a) => (
                    <SourceLink key={a.url} href={a.url} testid={`pmcpa-link-${a.label}`}>{a.label}</SourceLink>
                ))}
            </div>
        </div>
    );
}

function MethodologyLegend({ m }) {
    return (
        <div className="rounded-xl border border-[#1A4331]/20 bg-gradient-to-br from-[#F8F7F4] to-white p-5" data-testid="methodology-legend">
            <div className="flex items-center gap-2 mb-3">
                <Info className="w-4 h-4 text-[#1A4331]" />
                <h3 className="font-heading text-base font-medium text-[#1A4331]">
                    What&apos;s disclosed vs what&apos;s estimated
                </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <div className="mb-2"><MethodologyBadge factual /></div>
                    <ul className="space-y-1.5 text-sm text-[#1E293B]">
                        {m.factual.map((s) => (
                            <li key={s} className="flex gap-2 leading-relaxed">
                                <ShieldCheck className="w-3.5 h-3.5 text-[#166534] shrink-0 mt-1" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="mb-2"><MethodologyBadge factual={false} /></div>
                    <ul className="space-y-1.5 text-sm text-[#1E293B]">
                        {m.ai_estimated.map((s) => (
                            <li key={s} className="flex gap-2 leading-relaxed">
                                <Sparkles className="w-3.5 h-3.5 text-[#92400E] shrink-0 mt-1" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default function SourcesPanel({ drugId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!drugId) return;
        setLoading(true);
        setError(null);
        api.get(`/drugs/${drugId}/sources`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load sources"))
            .finally(() => setLoading(false));
    }, [drugId]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[#475569] py-8 justify-center" data-testid="sources-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Looking up disclosed records…
            </div>
        );
    }
    if (error) {
        return (
            <div className="rounded-lg bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] p-4 text-sm">
                {error}
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="space-y-5" data-testid="sources-panel">
            <MethodologyLegend m={data.methodology} />
            <DisclosureSection d={data.disclosure_uk} />
            <OpenPrescribingSection op={data.openprescribing} />
            <PmcpaSection p={data.pmcpa} />
        </div>
    );
}
